import React, { useState, useEffect } from "react";
import { useDados } from "../dados";
import {
  AlertTriangle,
  Check,
  CreditCard,
  Gauge,
  Landmark,
  Loader2,
  MessageCircle,
  Plus,
  RefreshCw,
  Save,
  ShieldCheck,
  Truck,
  XCircle,
} from "lucide-react";
import { Btn, Field, Pill, usarServidor } from "../ui/componentes";
import { brl } from "../ui/formato";

/* ══════════════════════════════════════════════════════════════════
   CONFIGURAÇÕES
   ══════════════════════════════════════════════════════════════════ */
function Config({ db, set, toast }) {
  const { acoes } = useDados();
  const { executar, ocupado } = usarServidor(toast);
  const [aba, setAba] = useState("empresa");
  /* Ligar a oficina ao fisco. Em Pouso Alegre a NFS-e NAO usa certificado:
     a prefeitura autentica por login e senha do portal dela, e ainda exige
     liberar "Utilizacao de RPS" la dentro. O certificado A1 serve para a
     NF-e de peca, que e estadual — e a oficina lancou 6 itens de peca
     contra 129 de servico, entao e a parte menos urgente. */
  const [fisco, setFisco] = useState({ usuario: "", senha: "", certSenha: "" });
  const [certArq, setCertArq] = useState(null);

  /**
   * Cadastro da oficina, do servidor.
   *
   * Era `useState(EMPRESA)` — um objeto fixo no código, com o CNPJ de uma
   * oficina que não existe. O dono editava, via um aviso de "salvo", e ao
   * recarregar estava tudo de volta: não havia rota nenhuma por trás.
   */
  const [emp, setEmp] = useState(null);
  const [conexao, setConexao] = useState(null);
  const [salvando, setSalvando] = useState(false);

  /**
   * Equipe, do servidor.
   *
   * A lista era montada no navegador e mostrava só quem estava logado; o
   * botão "Convidar pessoa" emitia o aviso "Convite enviado" e não chamava
   * nada. A oficina não conseguia cadastrar um mecânico.
   */
  const VAZIO_SERVICO = { cod: "", nome: "", h: "", valor: "" };
  const [novoServico, setNovoServico] = useState(VAZIO_SERVICO);
  const [salvandoServico, setSalvandoServico] = useState(false);

  /**
   * O botão "Novo serviço" só emitia o aviso "Serviço adicionado" e não
   * chamava nada — a tabela de mão de obra ficava vazia para sempre.
   */
  const criarServico = async () => {
    if (!novoServico.nome.trim()) {
      toast("Falta a descrição", "Diga qual é o serviço.", "erro"); return;
    }
    setSalvandoServico(true);
    try {
      await acoes.criarServico({
        cod: novoServico.cod.trim() || null,
        nome: novoServico.nome.trim(),
        // O balconista digita 1,5 — o servidor espera 1.5.
        h: String(novoServico.h).replace(",", "."),
        valor: String(novoServico.valor).replace(",", "."),
      });
      setNovoServico(VAZIO_SERVICO);
      toast("Serviço adicionado", "Já aparece na busca do orçamento e da OS.");
    } catch (e) {
      toast("Não deu para adicionar", e?.message ?? "Tente de novo.", "erro");
    } finally { setSalvandoServico(false); }
  };

  const VAZIO_MEMBRO = { nome: "", email: "", papel: "mecanico", senha: "" };
  const [equipe, setEquipe] = useState([]);
  const [novoMembro, setNovoMembro] = useState(VAZIO_MEMBRO);
  const [convidando, setConvidando] = useState(false);

  /**
   * O erro aparece na tela; não some num catch vazio.
   *
   * Antes era `catch { }` com o comentário "a aba mostra vazia". E mostrou:
   * `equipe_da_oficina()` falhava em toda chamada por uma ambiguidade de
   * nome no SQL, e a tela dizia "Ninguém cadastrado ainda" — que é
   * exatamente o que se espera de uma oficina nova. Ficou assim até alguém
   * cadastrar a segunda pessoa e estranhar.
   *
   * Lista vazia e lista que falhou ao carregar são coisas diferentes, e
   * agora a tela diz qual das duas é.
   */
  const [erroEquipe, setErroEquipe] = useState(null);
  const carregarEquipe = async () => {
    try {
      setEquipe(await acoes.verEquipe());
      setErroEquipe(null);
    } catch (e) {
      setErroEquipe(e?.message ?? "Não consegui carregar a equipe.");
    }
  };
  useEffect(() => { carregarEquipe(); }, []);

  const PAPEIS = [
    ["mecanico", "Mecânico", "OS, checklist, estoque e ditado por voz"],
    ["atendente", "Atendente", "Clientes, OS, notas e recebimentos"],
    ["financeiro", "Financeiro", "Contas a pagar e receber"],
    ["contador", "Contador", "Notas fiscais e relatórios"],
    ["admin", "Administrador", "Tudo, inclusive margens e financeiro"],
  ];
  const rotuloPapel = (p) =>
    p === "proprietario" ? "Proprietário" : (PAPEIS.find(([k]) => k === p)?.[1] ?? p);
  const enxerga = (p) =>
    p === "proprietario" ? "Tudo, e é dono da conta"
      : (PAPEIS.find(([k]) => k === p)?.[2] ?? "");

  const convidar = async () => {
    if (!novoMembro.nome.trim() || !novoMembro.email.trim()) {
      toast("Falta nome ou e-mail", "Os dois são obrigatórios para criar o acesso.", "erro"); return;
    }
    setConvidando(true);
    try {
      const r = await acoes.convidarPessoa({
        nome: novoMembro.nome.trim(), email: novoMembro.email.trim(),
        papel: novoMembro.papel, senha: novoMembro.senha || undefined,
      });
      setNovoMembro(VAZIO_MEMBRO);
      await carregarEquipe();
      toast("Pessoa cadastrada", r.senhaDefinida
        ? "Ela já pode entrar com o e-mail e a senha que você definiu."
        : "Falta definir a senha para ela conseguir entrar.");
    } catch (e) {
      toast("Não deu para cadastrar", e?.message ?? "Tente de novo.", "erro");
    } finally { setConvidando(false); }
  };

  const mudarMembro = async (id, campos, msg) => {
    try {
      setEquipe(await acoes.alterarMembro(id, campos));
      toast(msg, "Alteração aplicada.");
    } catch (e) {
      toast("Não deu para alterar", e?.message ?? "Tente de novo.", "erro");
    }
  };

  const [falhou, setFalhou] = useState(null);

  useEffect(() => {
    let vivo = true;
    // Engolir o erro deixaria a tela girando "Carregando" para sempre — que
    // é pior que dizer o que houve, porque não dá o que fazer a seguir.
    acoes.verOficina()
      .then((o) => { if (vivo) { setEmp(o); setFalhou(null); } })
      .catch((e) => vivo && setFalhou(e?.message ?? "Não consegui ler os dados da oficina."));
    acoes.diagnosticoFiscal().then((c) => vivo && setConexao(c)).catch(() => {});
    return () => { vivo = false; };
  }, []);

  const campo = (k) => (e) => setEmp({ ...emp, [k]: e.target.value });

  const salvar = async (campos, titulo) => {
    setSalvando(true);
    try {
      // Manda só o que a tela edita. Chave ausente mantém o valor no banco,
      // então nada é apagado por omissão.
      const o = await acoes.salvarOficina(campos);
      setEmp(o);
      setConexao(await acoes.diagnosticoFiscal().catch(() => conexao));
      toast(titulo, "As notas e os orçamentos já saem com as informações novas.");
    } catch (e) {
      toast("Não deu para salvar", e?.message ?? "Tente de novo.", "erro");
    } finally { setSalvando(false); }
  };

  if (falhou) {
    return <div className="card p-6 grid gap-2 justify-items-center text-center">
      <AlertTriangle size={20} className="t-crit" />
      <div className="text-sm font-semibold">{falhou}</div>
      <div className="text-xs t-mut">Os outros módulos seguem funcionando.</div>
    </div>;
  }

  if (!emp) {
    return <div className="card p-6 flex items-center justify-center gap-2 t-mut">
      <Loader2 size={16} className="animate-spin" /> Carregando os dados da oficina…
    </div>;
  }
  const abas = [["empresa", "Dados da oficina"], ["fiscal", "Fiscal"], ["equipe", "Equipe"], ["servicos", "Tabela de serviços"], ["integracoes", "Integrações"]];

  return (
    <div className="grid gap-4">
      <div className="flex gap-1 scroll" style={{ overflowX: "auto", borderBottom: "1px solid var(--line)" }}>
        {abas.map(([k, l]) => <button key={k} className={`tab ${aba === k ? "on" : ""}`} onClick={() => setAba(k)}>{l}</button>)}
      </div>

      {aba === "empresa" && (
        <div className="card p-5">
          <div className="grid gap-3.5" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))" }}>
            <Field label="Razão social"><input className="inp" value={emp.razao_social ?? ""} onChange={campo("razao_social")} /></Field>
            <Field label="Nome fantasia"><input className="inp" value={emp.nome_fantasia ?? ""} onChange={campo("nome_fantasia")} /></Field>

            {/* CNPJ e município ficam travados: o CNPJ é a identidade de quem
                emite a nota, e o código IBGE escolhe a prefeitura que recebe a
                NFS-e. O banco recusa os dois — o campo desabilitado só conta a
                mesma história antes de a pessoa tentar. */}
            <Field label="CNPJ" hint="Não muda por aqui — é a identidade fiscal.">
              <input className="inp inp-mono" value={emp.cnpj ?? ""} disabled /></Field>
            <Field label="Município / UF" hint="Define a prefeitura da NFS-e. Correção é com o suporte.">
              <input className="inp" value={(emp.municipio ?? "") + " / " + (emp.uf ?? "")} disabled /></Field>

            <Field label="Inscrição estadual" hint="Escreva ISENTO se a oficina for isenta. Sem inscrição não sai NF-e de peça — peça aplicada é ICMS.">
              <input className="inp inp-mono" value={emp.inscricao_estadual ?? ""} onChange={campo("inscricao_estadual")} /></Field>
            <Field label="Inscrição municipal" hint="Escreva ISENTO se não houver. Sem inscrição não sai NFS-e de serviço.">
              <input className="inp inp-mono" value={emp.inscricao_municipal ?? ""} onChange={campo("inscricao_municipal")} /></Field>

            <Field label="Telefone"><input className="inp inp-mono" value={emp.telefone ?? ""} onChange={campo("telefone")} /></Field>
            <Field label="E-mail"><input className="inp" value={emp.email ?? ""} onChange={campo("email")} /></Field>
            <Field label="Logradouro" className="col-span-full"><input className="inp" value={emp.logradouro ?? ""} onChange={campo("logradouro")} /></Field>
            <Field label="Número"><input className="inp" value={emp.numero ?? ""} onChange={campo("numero")} /></Field>
            <Field label="Complemento"><input className="inp" value={emp.complemento ?? ""} onChange={campo("complemento")} /></Field>
            <Field label="Bairro"><input className="inp" value={emp.bairro ?? ""} onChange={campo("bairro")} /></Field>
            <Field label="CEP"><input className="inp inp-mono" value={emp.cep ?? ""} onChange={campo("cep")} /></Field>
          </div>
          <div className="flex justify-end mt-4">
            <Btn variant="p" icon={Save} disabled={salvando} onClick={() => salvar({
              razao_social: emp.razao_social, nome_fantasia: emp.nome_fantasia,
              inscricao_estadual: emp.inscricao_estadual, inscricao_municipal: emp.inscricao_municipal,
              telefone: emp.telefone, email: emp.email,
              logradouro: emp.logradouro, numero: emp.numero, complemento: emp.complemento,
              bairro: emp.bairro, cep: emp.cep,
            }, "Dados salvos")}>{salvando ? "Salvando…" : "Salvar alterações"}</Btn>
          </div>
        </div>
      )}

      {aba === "fiscal" && (
        <div className="grid gap-4">
          {/* Esta faixa afirmava "Certificado A1 instalado" e "Conectado à
              SEFAZ-MG" sem consultar nada — dizia o contrário do que a API
              respondia. Agora é o diagnóstico real de /fiscal/conexao. */}
          <div className="card p-4 flex flex-wrap items-center gap-3">
            {conexao?.pronto
              ? <ShieldCheck size={20} className="t-ok" />
              : <AlertTriangle size={20} className="t-warn" />}
            <div className="flex-1">
              <div className="text-sm font-semibold">
                {conexao?.certificadoValido ? "Certificado digital A1 instalado" : "Certificado digital pendente"}
              </div>
              <div className="text-xs t-mut">
                {conexao?.proximoPasso ?? "Consultando o provedor fiscal…"}
              </div>
            </div>
            {conexao && (
              <Pill t={conexao.pronto ? "ok" : "warn"} icon={conexao.pronto ? Check : AlertTriangle}>
                {conexao.pronto ? "Pronto para emitir" : "Emissão bloqueada"}
              </Pill>
            )}
            <Btn variant="o" size="xs" icon={RefreshCw} onClick={async () => {
              setConexao(await acoes.diagnosticoFiscal());
              toast("Diagnóstico atualizado", "Estado lido do provedor agora.");
            }}>Verificar</Btn>
          </div>

          {/* As rotas de conexao existiam no servidor desde sempre e nenhuma
              tela chamava: dava para ler "certificado pendente" e nao haver
              onde resolver. Em 66 OS entregues, zero notas sairam. */}
          <div className="card p-4 grid gap-4">
            {conexao && conexao.provedorConfigurado === false && (
              <div className="p-3" style={{ borderLeft: "3px solid var(--crit)", background: "var(--crit-l)", borderRadius: "var(--r)" }}>
                <div className="text-sm font-semibold t-crit">Nenhuma nota sai deste servidor ainda</div>
                <div className="text-sm t-mut mt-1">
                  Falta a credencial do provedor fiscal. Em homologação o sistema apenas
                  <strong> simula</strong> a emissão — e nota simulada não existe na prefeitura,
                  por mais que a tela diga "autorizada". Em produção, a emissão agora é recusada
                  em vez de fingir.
                </div>
              </div>
            )}

            <div>
              <div className="eyebrow mb-1">Ligar ao fisco</div>
              <p className="text-sm t-mut">
                Em Pouso Alegre a nota de servico e autenticada por <strong>login e senha do
                portal da prefeitura</strong>, nao por certificado. Antes de tudo e preciso
                liberar la: <span className="mono">Login &rsaquo; Autorizacao &rsaquo; Utilizacao de RPS</span>.
              </p>
            </div>

            <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))" }}>
              <Field label="Usuario do portal da prefeitura">
                <input className="inp" value={fisco.usuario} autoComplete="off"
                  onChange={(e) => setFisco((p) => ({ ...p, usuario: e.target.value }))} />
              </Field>
              <Field label="Senha do portal">
                <input className="inp" type="password" value={fisco.senha} autoComplete="new-password"
                  onChange={(e) => setFisco((p) => ({ ...p, senha: e.target.value }))} />
              </Field>
            </div>

            <div className="flex flex-wrap gap-2">
              <Btn variant="p" icon={ShieldCheck} disabled={ocupado} onClick={() => executar(
                () => acoes.cadastrarEmpresaFiscal({
                  cnpj: db.oficina?.cnpj, razaoSocial: db.oficina?.razao_social,
                  nomeFantasia: db.oficina?.nome_fantasia,
                  inscricaoEstadual: db.oficina?.inscricao_estadual,
                  inscricaoMunicipal: db.oficina?.inscricao_municipal,
                  cep: db.oficina?.cep, logradouro: db.oficina?.logradouro,
                  numero: db.oficina?.numero, bairro: db.oficina?.bairro,
                  municipio: db.oficina?.municipio, uf: db.oficina?.uf,
                  codigoIbge: db.oficina?.codigo_ibge, regime: db.oficina?.regime,
                  email: db.oficina?.email, telefone: db.oficina?.telefone,
                  usuarioPrefeitura: fisco.usuario || undefined,
                  senhaPrefeitura: fisco.senha || undefined,
                }),
                async () => {
                  setFisco((p) => ({ ...p, senha: "" }));
                  setConexao(await acoes.diagnosticoFiscal());
                  toast("Oficina cadastrada no provedor", "Agora registre o retorno das notas.");
                },
                "Nao deu para cadastrar a oficina")}>Cadastrar a oficina</Btn>

              <Btn variant="o" icon={RefreshCw} disabled={ocupado} onClick={() => executar(
                () => acoes.registrarRetornoFiscal(),
                async () => {
                  setConexao(await acoes.diagnosticoFiscal());
                  toast("Retorno registrado", "O provedor avisa este sistema quando a prefeitura responder.");
                },
                "Nao deu para registrar o retorno")}>Registrar retorno</Btn>
            </div>

            <div style={{ borderTop: "1px solid var(--line)", paddingTop: 14 }}>
              <div className="eyebrow mb-1">Certificado A1 — so para nota de peca</div>
              <p className="text-sm t-mut mb-3">
                A NF-e de peca e estadual e exige o certificado. Tem de ser <strong>A1</strong>:
                o A3 mora em token ou cartao fisico, e nao ha como plugar hardware num servidor.
                O arquivo atravessa o sistema e vai direto ao provedor — nunca fica gravado aqui.
              </p>
              <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))" }}>
                <Field label="Arquivo .pfx ou .p12">
                  <input className="inp" type="file" accept=".pfx,.p12"
                    onChange={(e) => setCertArq(e.target.files?.[0] ?? null)} />
                </Field>
                <Field label="Senha do certificado">
                  <input className="inp" type="password" value={fisco.certSenha} autoComplete="new-password"
                    onChange={(e) => setFisco((p) => ({ ...p, certSenha: e.target.value }))} />
                </Field>
              </div>
              <div className="mt-3">
                <Btn variant="o" icon={ShieldCheck} disabled={ocupado || !certArq} onClick={() => executar(
                  async () => {
                    const b64 = await new Promise((ok, falhou) => {
                      const leitor = new FileReader();
                      leitor.onload = () => ok(String(leitor.result).split(",")[1] ?? "");
                      leitor.onerror = () => falhou(new Error("Nao consegui ler o arquivo do certificado."));
                      leitor.readAsDataURL(certArq);
                    });
                    return acoes.enviarCertificado(b64, fisco.certSenha);
                  },
                  async () => {
                    setCertArq(null);
                    setFisco((p) => ({ ...p, certSenha: "" }));
                    setConexao(await acoes.diagnosticoFiscal());
                    toast("Certificado enviado", "Validade registrada. O arquivo nao ficou guardado aqui.");
                  },
                  "Nao deu para enviar o certificado")}>Enviar certificado</Btn>
              </div>
            </div>

            {conexao?.pronto && conexao?.ambiente !== "producao" && (
              <div style={{ borderTop: "1px solid var(--line)", paddingTop: 14 }}>
                <p className="text-sm t-mut mb-2">
                  Esta em <strong>homologacao</strong> — as notas sao de teste e nao valem nada.
                  Emita uma de teste e confira com o contador antes de virar.
                </p>
                <Btn variant="x" icon={AlertTriangle} disabled={ocupado} onClick={() => executar(
                  () => acoes.ativarProducaoFiscal(),
                  async () => {
                    setConexao(await acoes.diagnosticoFiscal());
                    toast("Producao ativada", "A partir de agora as notas valem de verdade.", "alerta");
                  },
                  "Nao deu para ativar a producao")}>Ativar producao</Btn>
              </div>
            )}
          </div>

          {conexao && (
            <div className="card p-4 grid gap-2 text-sm">
              {[["Credencial do provedor neste servidor", conexao.provedorConfigurado],
                ["Empresa cadastrada no provedor", conexao.empresaCadastrada],
                /* Só é exigência quando há NF-e de peça: a NFS-e de Pouso
                   Alegre autentica por login do portal, sem certificado. */
                [conexao.certificadoNecessario
                  ? "Certificado A1 válido (exigido pela NF-e de peça)"
                  : "Certificado A1 (não exigido para a nota de serviço daqui)",
                 conexao.certificadoNecessario ? conexao.certificadoValido : true],
                ["Retorno das notas registrado", conexao.webhookRegistrado],
                ["NF-e de peça habilitada", conexao.emiteNfe],
                ["NFS-e de serviço habilitada", conexao.emiteNfse]].map(([l, v]) => (
                <div key={l} className="flex items-center gap-2">
                  {v ? <Check size={14} className="t-ok" /> : <XCircle size={14} className="t-mut2" />}
                  <span className={v ? "" : "t-mut"}>{l}</span>
                </div>
              ))}
              {conexao.diasParaVencer != null && conexao.diasParaVencer > 0 && (
                <div className="text-xs t-mut2 mt-1">Certificado vence em {conexao.diasParaVencer} dia(s).</div>
              )}
            </div>
          )}

          <div className="card p-5">
            <div className="grid gap-3.5" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))" }}>
              <Field label="Regime tributário">
                <select className="inp" value={emp.regime ?? "simples_nacional"} onChange={campo("regime")}>
                  <option value="simples_nacional">Simples Nacional</option>
                  <option value="lucro_presumido">Lucro Presumido</option>
                  <option value="lucro_real">Lucro Real</option>
                </select>
              </Field>
              <Field label="Alíquota de ISS (%)" hint="Serviços — NFS-e municipal.">
                <input className="inp inp-mono" value={emp.aliquota_iss ?? ""} onChange={campo("aliquota_iss")} /></Field>
              <Field label="CNAE"><input className="inp inp-mono" value={emp.cnae ?? ""} onChange={campo("cnae")} /></Field>
              <Field label="Código do serviço (LC 116)" hint="14.01 — lubrificação, limpeza, revisão e conserto de veículos.">
                <input className="inp inp-mono" value={emp.codigo_servico_lc116 ?? ""} onChange={campo("codigo_servico_lc116")} /></Field>
              <Field label="Ambiente" hint="Muda pelo provedor, não por aqui.">
                <input className="inp" value={emp.fiscal_ambiente ?? ""} disabled /></Field>
            </div>
            <div className="flex justify-end mt-4">
              <Btn variant="p" icon={Save} disabled={salvando} onClick={() => salvar({
                regime: emp.regime, aliquota_iss: emp.aliquota_iss,
                cnae: emp.cnae, codigo_servico_lc116: emp.codigo_servico_lc116,
              }, "Parâmetros fiscais salvos")}>{salvando ? "Salvando…" : "Salvar"}</Btn>
            </div>
          </div>
        </div>
      )}

      {aba === "equipe" && (
        <div className="grid gap-4">
          <div className="card p-5">
            <div className="dsp mb-1" style={{ fontSize: 15 }}>Cadastrar pessoa</div>
            <p className="text-sm t-mut mb-4">
              O sistema ainda não envia e-mail. Defina a senha aqui, com a pessoa do lado,
              e peça para ela trocar no primeiro acesso.
            </p>
            <div className="grid gap-3.5" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))" }}>
              <Field label="Nome">
                <input className="inp" value={novoMembro.nome}
                  onChange={(e) => setNovoMembro({ ...novoMembro, nome: e.target.value })} /></Field>
              <Field label="E-mail" hint="É com ele que a pessoa entra.">
                <input className="inp" value={novoMembro.email} inputMode="email"
                  onChange={(e) => setNovoMembro({ ...novoMembro, email: e.target.value })} /></Field>
              <Field label="Cargo" hint={enxerga(novoMembro.papel)}>
                <select className="inp" value={novoMembro.papel}
                  onChange={(e) => setNovoMembro({ ...novoMembro, papel: e.target.value })}>
                  {PAPEIS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select></Field>
              <Field label="Senha" hint="Mínimo 10 caracteres. Pode deixar em branco e definir depois.">
                <input className="inp" type="text" value={novoMembro.senha}
                  onChange={(e) => setNovoMembro({ ...novoMembro, senha: e.target.value })} /></Field>
            </div>
            <div className="flex justify-end mt-4">
              <Btn variant="p" icon={Plus} disabled={convidando} onClick={convidar}>
                {convidando ? "Cadastrando…" : "Cadastrar pessoa"}</Btn>
            </div>
          </div>

          <div className="card" style={{ overflow: "hidden" }}>
            <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: "1px solid var(--line)" }}>
              <span className="dsp" style={{ fontSize: 15 }}>Quem tem acesso</span>
              <span className="text-xs t-mut2">{equipe.length} pessoa(s)</span>
            </div>
            {erroEquipe ? (
              <div className="p-6 text-center text-sm">
                <div className="t-crit font-semibold mb-1">Não consegui carregar a equipe</div>
                <div className="t-mut">{erroEquipe}</div>
                <Btn variant="o" size="xs" icon={RefreshCw} className="mt-3"
                  onClick={carregarEquipe}>Tentar de novo</Btn>
              </div>
            ) : equipe.length === 0 ? (
              <div className="p-6 text-center text-sm t-mut">Ninguém cadastrado ainda.</div>
            ) : (
              <div className="scroll">
                <table className="tbl">
                  <thead><tr><th>Pessoa</th><th>Cargo</th><th>Enxerga</th><th>Situação</th><th></th></tr></thead>
                  <tbody>
                    {equipe.map((u) => (
                      <tr key={u.usuario_id}>
                        <td>
                          <div className="text-sm font-semibold">{u.nome}</div>
                          <div className="text-xs t-mut mono">{u.email}</div>
                        </td>
                        <td className="text-sm t-mut">{rotuloPapel(u.papel)}</td>
                        <td className="text-sm t-mut">{enxerga(u.papel)}</td>
                        <td>
                          {!u.ativo ? <Pill t="mut">Sem acesso</Pill>
                            : !u.tem_senha ? <Pill t="warn" icon={AlertTriangle}>Falta a senha</Pill>
                            : <Pill t="ok" icon={Check}>Ativo</Pill>}
                        </td>
                        <td>
                          {u.papel !== "proprietario" && (
                            <Btn variant="g" size="xs"
                              onClick={() => mudarMembro(u.usuario_id, { ativo: !u.ativo },
                                u.ativo ? "Acesso removido" : "Acesso liberado")}>
                              {u.ativo ? "Remover acesso" : "Liberar"}
                            </Btn>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="text-xs t-mut2">
            Quem aparece como <strong>Falta a senha</strong> está cadastrado mas ainda não
            consegue entrar. Cadastre a pessoa de novo com a senha preenchida para defini-la.
          </div>
        </div>
      )}

      {aba === "servicos" && (
        <div className="grid gap-4">
          <div className="card p-5">
            <div className="dsp mb-1" style={{ fontSize: 15 }}>Novo serviço</div>
            <p className="text-sm t-mut mb-4">
              O que entra aqui aparece na busca do orçamento e da OS. O valor por hora
              é calculado — informe o tempo e o preço cobrado.
            </p>
            <div className="grid gap-3.5" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))" }}>
              <Field label="Código" hint="Como você chama internamente. Ex.: MO-001.">
                <input className="inp inp-mono" value={novoServico.cod}
                  onChange={(e) => setNovoServico({ ...novoServico, cod: e.target.value })} /></Field>
              <Field label="Descrição" className="col-span-full">
                <input className="inp" value={novoServico.nome}
                  onChange={(e) => setNovoServico({ ...novoServico, nome: e.target.value })} /></Field>
              <Field label="Tempo (horas)" hint="Use ponto ou vírgula: 1,5">
                <input className="inp inp-mono" value={novoServico.h} inputMode="decimal"
                  onChange={(e) => setNovoServico({ ...novoServico, h: e.target.value })} /></Field>
              <Field label="Valor cobrado (R$)">
                <input className="inp inp-mono" value={novoServico.valor} inputMode="decimal"
                  onChange={(e) => setNovoServico({ ...novoServico, valor: e.target.value })} /></Field>
            </div>
            <div className="flex justify-end mt-4">
              <Btn variant="p" icon={Plus} disabled={salvandoServico} onClick={criarServico}>
                {salvandoServico ? "Salvando…" : "Adicionar serviço"}</Btn>
            </div>
          </div>

          <div className="card" style={{ overflow: "hidden" }}>
            <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: "1px solid var(--line)" }}>
              <span className="dsp" style={{ fontSize: 15 }}>Tabela de mão de obra</span>
              <span className="text-xs t-mut2">{db.servicos.length} serviço(s)</span>
            </div>
            {db.servicos.length === 0 ? (
              <div className="p-6 text-center text-sm t-mut">
                Nenhum serviço cadastrado. Comece pelos mais comuns da oficina.
              </div>
            ) : (
              <div className="scroll">
                <table className="tbl">
                  <thead><tr><th>Código</th><th>Serviço</th><th className="num">Tempo</th><th className="num">Valor</th><th className="num">Valor / hora</th></tr></thead>
                  <tbody>
                    {db.servicos.map((sv) => (
                      <tr key={sv.id}>
                        <td className="mono text-xs t-mut">{sv.cod}</td>
                        <td className="text-sm font-medium">{sv.nome}</td>
                        <td className="num text-sm t-mut">{String(sv.h).replace(".", ",")} h</td>
                        <td className="num text-sm font-semibold">{brl(sv.valor)}</td>
                        <td className="num text-sm t-mut">{sv.h ? brl(sv.valor / sv.h) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {aba === "integracoes" && (
        <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))" }}>
          {[
            { i: MessageCircle, n: "WhatsApp Business", d: "Envia orçamento, aprovação com assinatura e lembrete de revisão.", on: true },
            { i: Landmark, n: "SEFAZ-MG e prefeitura", d: "Emissão de NF-e e NFS-e com certificado A1.", on: true },
            { i: CreditCard, n: "Maquininha e PIX", d: "Baixa automática do recebimento na conta a receber.", on: true },
            { i: Truck, n: "Catálogo de fornecedores", d: "Preço e disponibilidade em tempo real no pedido de compra.", on: false },
            { i: Gauge, n: "Scanner OBD-II", d: "Importa os códigos de falha direto para a OS.", on: false },
            { i: Save, n: "Backup em nuvem", d: "Cópia diária de todo o histórico da oficina.", on: true },
          ].map((x) => (
            <div key={x.n} className="card p-4">
              <div className="flex items-start gap-3">
                <div className="flex items-center justify-center flex-none" style={{ width: 34, height: 34, borderRadius: 6, background: x.on ? "var(--gulf-l)" : "var(--el)", color: x.on ? "var(--gulf-d)" : "var(--mut2)" }}><x.i size={17} /></div>
                <div className="flex-1">
                  <div className="text-sm font-semibold">{x.n}</div>
                  <div className="text-xs t-mut mt-0.5" style={{ lineHeight: 1.45 }}>{x.d}</div>
                </div>
              </div>
              <div className="flex items-center justify-between mt-3 pt-3" style={{ borderTop: "1px solid var(--line)" }}>
                {x.on ? <Pill t="ok" icon={Check}>Ativa</Pill> : <Pill t="mut">Não conectada</Pill>}
                <Btn variant={x.on ? "g" : "o"} size="xs" onClick={() => toast("Ainda não disponível", `A ligação com ${x.n} ainda não foi feita. O botão fica aqui para quando for.`, "alerta")}>{x.on ? "Configurar" : "Conectar"}</Btn>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export { Config };
